'use client'
// =============================================================================
// AgentCard — widget CORE (right-panel) : identité + état réel de
// l'orchestrateur (Ti-Coune), dérivé des missions réelles — jamais un statut
// fabriqué. État WORKING/READY = présence d'une mission en EXECUTION (même
// signal que MissionCard/ancien Footer, pas une 2e source de vérité).
// "Dernière action" = mission la plus récemment mise à jour, pas un texte
// inventé.
// =============================================================================
import { useState, useEffect, useCallback } from 'react'
import { useSession } from 'next-auth/react'

const CYAN  = '#00d4ff'
const GREEN = '#34d399'

type MissionStatus =
  | 'NEW' | 'ANALYSIS' | 'PLANNING' | 'EXECUTION' | 'VALIDATION'
  | 'COMPLETED' | 'WAITING_USER' | 'FAILED' | 'CANCELLED'

interface MissionLite {
  id: string; objective: string; status: MissionStatus; updatedAt: string
}

function fmtAgo(iso?: string | null): string {
  if (!iso) return '—'
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000))
  if (s < 60) return `il y a ${s}s`
  const m = Math.round(s / 60)
  if (m < 60) return `il y a ${m} min`
  return `il y a ${Math.round(m / 60)} h`
}

export function AgentCard() {
  const { data: session } = useSession()
  const [missions, setMissions] = useState<MissionLite[] | null>(null)
  const [, setTick] = useState(0)

  const refresh = useCallback(() => {
    fetch('/api/missions?limit=500')
      .then(r => (r.ok ? r.json() : []))
      .then(d => setMissions(Array.isArray(d) ? d : []))
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!session) return
    refresh()
    const iv = setInterval(refresh, 15_000)
    return () => clearInterval(iv)
  }, [session, refresh])

  useEffect(() => {
    const iv = setInterval(() => setTick(t => t + 1), 1000)
    return () => clearInterval(iv)
  }, [])

  if (!session) return null

  const working = (missions ?? []).some(m => m.status === 'EXECUTION')
  const lastMission = (missions ?? [])
    .slice()
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0]
  const color = working ? CYAN : GREEN

  return (
    <div style={{
      padding: '12px 14px', borderRadius: 8,
      background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
      marginBottom: 10, fontFamily: 'var(--font-mono)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 9, letterSpacing: '0.12em', color: 'rgba(255,255,255,0.3)' }}>
          ACTIVE AGENT
        </div>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 8, color }}>
          <span style={{
            width: 5, height: 5, borderRadius: '50%', background: color,
            boxShadow: `0 0 6px ${color}`, display: 'inline-block',
            animation: working ? 'pulse-dot 1.4s infinite' : 'none',
          }} />
          {working ? 'WORKING' : 'READY'}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>🤖 Ti-Coune</span>
      </div>
      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
        Orchestrateur ReAct Amiral Blanc
      </div>

      {lastMission && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.2)', marginBottom: 2 }}>Dernière action</div>
          <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.55)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {lastMission.objective}
          </div>
          <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.2)', marginTop: 1 }}>{fmtAgo(lastMission.updatedAt)}</div>
        </div>
      )}
    </div>
  )
}
